// Rating a visit (docs/plans/phase3-portal-feedback-kiswahili.md §5.2): five ratings, each
// with its words so a number is never the only signal, and an optional comment. Used in the
// portal and on the single-use SMS link page.
import { FEEDBACK_COMMENT_MAX, type SubmitFeedbackBody } from '@wodi/shared';
import { Button, Notice, RadioGroup, TextArea } from '@wodi/ui';
import { useState } from 'react';

import { useOnline } from '../hooks/use-online.ts';
import { useT } from '../i18n/i18n.tsx';
import type { MessageKey } from '../i18n/messages/en.ts';
import styles from './portal.module.css';

export const RATING_KEYS: Record<number, MessageKey> = {
  1: 'feedback.rating.1',
  2: 'feedback.rating.2',
  3: 'feedback.rating.3',
  4: 'feedback.rating.4',
  5: 'feedback.rating.5',
};

export function FeedbackForm({
  onSubmit,
  isPending,
  error,
}: {
  onSubmit: (body: SubmitFeedbackBody) => void;
  isPending: boolean;
  error: string | null;
}) {
  const t = useT();
  const online = useOnline();
  const [rating, setRating] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [missing, setMissing] = useState(false);
  return (
    <form
      className={styles.form}
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        if (rating === null) {
          setMissing(true);
          return;
        }
        const trimmed = comment.trim();
        onSubmit({
          rating: Number(rating),
          ...(trimmed === '' ? {} : { comment: trimmed }),
        });
      }}
    >
      {error === null ? null : <Notice tone="error">{error}</Notice>}
      <RadioGroup
        label={t('feedback.rating')}
        options={[5, 4, 3, 2, 1].map((n) => ({
          id: String(n),
          label: `${String(n)}: ${t(RATING_KEYS[n] ?? 'feedback.rating.3')}`,
        }))}
        value={rating}
        onChange={(value) => {
          setRating(value);
          setMissing(false);
        }}
        {...(missing ? { errorMessage: t('feedback.ratingMissing') } : {})}
      />
      <TextArea
        label={t('feedback.comment')}
        description={t('feedback.commentHelp')}
        rows={4}
        maxLength={FEEDBACK_COMMENT_MAX}
        value={comment}
        onChange={setComment}
      />
      <div className={styles.actions}>
        <Button
          type="submit"
          variant="primary"
          isDisabled={!online}
          isPending={isPending}
          pendingLabel={t('feedback.sending')}
        >
          {t('feedback.send')}
        </Button>
      </div>
    </form>
  );
}
